import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { API_URL } from "@/config";

const EASE = [0.16, 1, 0.3, 1];

const MOODS = [
  { max: 0, label: "flatlining." },
  { max: 2, label: "really bad. like, 3am-phone-scroll bad." },
  { max: 4, label: "surviving on caffeine and vibes." },
  { max: 6, label: "could be worse. could be better." },
  { max: 8, label: "decent. now imagine great." },
  { max: 9, label: "so close to perfect." },
  { max: 10, label: "sleeping like a baby. teach us." },
];

export default function SleepScale({ value, onChange }) {
  const [hover, setHover] = useState(null);
  const [stats, setStats] = useState(null);
  const active = hover != null ? hover : value;

  useEffect(() => {
    fetch(`${API_URL}/waitlist/sleep-stats`)
      .then((r) => r.json())
      .then((d) => d.available && setStats(d))
      .catch(() => {});
  }, []);

  return (
    <section
      id="sleep-scale"
      data-testid="sleep-scale-section"
      className="mx-auto max-w-6xl px-6 py-24 sm:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="text-center"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#171512]/50">
          before anything
        </p>
        <h2 className="mx-auto mt-6 max-w-2xl font-display text-3xl font-medium leading-[1.1] tracking-tight text-[#171512] sm:text-5xl">
          How’s Your <span className="font-serif italic">Sleep Cycle?</span>
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
        className="mx-auto mt-14 max-w-2xl"
      >
        <div className="flex items-end justify-between gap-1 sm:gap-2">
          {Array.from({ length: 11 }, (_, i) => {
            const filled = active != null && i <= active;
            const size = 10 + i * 1.4;
            return (
              <button
                key={i}
                type="button"
                data-testid={`sleep-dot-${i}`}
                onClick={() => onChange(i)}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                aria-label={`Rate your sleep ${i} out of 10`}
                className="flex flex-1 flex-col items-center gap-2 pb-1"
              >
                <motion.span
                  animate={{ scale: value === i ? 1.3 : 1 }}
                  transition={{ type: "spring", stiffness: 320, damping: 14 }}
                  style={{
                    width: size,
                    height: size,
                    background: filled
                      ? `hsl(340, ${48 + i * 2}%, ${89 - i * 1.7}%)`
                      : "#EAE7DC",
                  }}
                  className="block rounded-full transition-colors duration-200"
                />
                <span className="font-mono text-[9px] text-[#171512]/35">{i}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-[#171512]/40">
          <span className="text-left">
            really bad
            <span className="mt-1 block lowercase tracking-[0.08em] text-[#171512]/30">
              (send help)
            </span>
          </span>
          <span>very good</span>
        </div>

        <p
          data-testid="sleep-rating-label"
          className="mt-8 min-h-7 text-center font-serif text-xl italic text-[#171512]/80"
        >
          {value != null ? MOODS.find((m) => value <= m.max).label : "Tap a dot. be honest."}
        </p>

        {value != null && (
          <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#B9822E]">
            saved with your signup below
          </p>
        )}

        {stats && (
          <p
            data-testid="sleep-community-average"
            className="mt-6 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-[#171512]/50"
          >
            community average: {stats.average}/10 across {stats.count} honest answers
          </p>
        )}
      </motion.div>
    </section>
  );
}
